import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Form } from 'react-form';
import { parsePhoneNumber } from 'react-phone-number-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';

import * as queryString from 'query-string';
import { getTennisAustraliaUserProfile, confirmTennisAustraliaUser } from '../../api/oauthApi';
import { getApiAxiosInstance } from '../../utils/network';
import { getApiErrorMessage } from '../../utils/apiError';
import {
    Input,
    PhoneNumber,
    Select,
} from '../../components/Form';
import { createReactFormValidator, required, requiredEmail } from '../../utils/validation';
import { intlPhoneNumber } from '../../utils/validation-libdep';
import { GenderField } from '../../components/OAuthConfirmation';
import { getYearOptions, getDayOptions, months, daysPerMonth } from '../../utils/calendar';
import { showErrorPopup } from '../../actions/notification-actions';

import config from '../../../config';
import styles from './tennisAustraliaAuthConfirmationPage.scss';

const TennisAustraliaAuthConfirmationPage = () => {
    const location = useLocation();
    const { state } = queryString.parse(location.search);
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [playerProfile, setPlayerProfile] = useState(null);
    const [checked, setChecked] = useState(false);
    const [days, setDays] = useState(31);

    useEffect(() => {
        const getProfileConfirmation = async () => {
            setIsLoading(true);

            try {
                const axiosApiInstance = getApiAxiosInstance();
                const response = await getTennisAustraliaUserProfile(axiosApiInstance, state);

                if (response?.data) {
                    setPlayerProfile(response?.data);

                    // Initialize days count from birthDate
                    if (response.data.birthDate) {
                        const parsed = moment(response.data.birthDate);
                        if (parsed.isValid()) {
                            setDays(daysPerMonth[parsed.month()]);
                        }
                    }

                    setIsLoading(false);
                }
            } catch (error) {
                setIsLoading(false);
                dispatch(showErrorPopup({ showSupportLink: true }));
            }
        };

        if (state) {
            getProfileConfirmation();
        } else {
            dispatch(showErrorPopup({ showSupportLink: true }));
        }
    }, []);

    const handleOnTermsCheckChange = () => {
        setChecked(!checked);
    };

    const handleMonthChange = (val) => {
        const monthIndex = months.indexOf(val);
        if (monthIndex !== -1) {
            setDays(daysPerMonth[monthIndex]);
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);

        const monthIndex = months.indexOf(values.month);
        const birthDate = new Date(values.year, monthIndex, values.day).toISOString();

        const payload = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phone,
            phoneCountry: parsePhoneNumber(values.phone)?.country || 'AU',
            birthDate,
            sex: values.gender,
        };

        try {
            const axiosApiInstance = getApiAxiosInstance();
            const response = await confirmTennisAustraliaUser(axiosApiInstance, state, payload);
            if (response?.data?.redirectTo) {
                window.location.href = response.data.redirectTo;
            }
            setSubmitting(false);
        } catch (error) {
            setSubmitting(false);
            const errorMessage = getApiErrorMessage(error);
            dispatch(showErrorPopup({
                error: errorMessage || 'Something went wrong, please try again or contact support',
            }));
        }
    };

    const years = getYearOptions();
    const dayValues = getDayOptions(days);

    if (isLoading || !playerProfile) {
        return (
            <div className="container d-flex jcc">
                <div className="d-flex jcc mt48">
                    <span data-testid="tennis-australia-confirmation.page.loading">
                        <i className="fas fa-circle-notch fa-spin" />{' '}
                        Loading...
                    </span>
                </div>
            </div>
        );
    }

    const parsedDate = playerProfile?.birthDate ? moment(playerProfile.birthDate) : null;

    // Convert local AU phone number (e.g. '0491 570 156') to international format ('+61491570156')
    const INTERNATIONAL_FORMAT = 'E.164';
    const parsedPhone = playerProfile?.mobileNumber
        ? parsePhoneNumberFromString(playerProfile.mobileNumber, 'AU')
        : null;

    const defaultValues = {
        firstName: playerProfile?.firstName || '',
        lastName: playerProfile?.lastName || '',
        email: playerProfile?.emailAddress || '',
        phone: parsedPhone ? parsedPhone.format(INTERNATIONAL_FORMAT) : playerProfile?.mobileNumber || '',
        month: parsedDate ? months[parsedDate.month()] : null,
        day: parsedDate ? parsedDate.date() : null,
        year: parsedDate ? parsedDate.year() : null,
        gender: playerProfile?.competitionCategory === 'Male' ? 'M' : playerProfile?.competitionCategory === 'Female' ? 'F' : '',
    };

    return (
        <div className="container d-flex jcc">
            <div className={styles.wrapper} data-testid="tennis-australia-confirmation.page.wrapper">
                <div className={styles.title} data-testid="tennis-australia-confirmation.page.title">
                    Activate UTR Profile
                </div>
                <p className={styles.content} data-testid="tennis-australia-confirmation.page.description">Please review and agree to the UTR <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid="tennis-australia-confirmation.page.tos-link">Terms of Service</a> to activate your account using your information.</p>
                <Form
                    onSubmit={handleSubmit}
                    defaultValues={defaultValues}
                >
                    {(formApi) => {
                        const { submitForm, values, touched, getWarning, getError } = formApi;

                        return (
                            <form onSubmit={submitForm} data-testid="tennis-australia-confirmation.page.form">
                                <div className="row">
                                    <div className="col-12">
                                        <Input
                                            id="firstNameInput"
                                            field="firstName"
                                            label="First Name"
                                            validate={createReactFormValidator(required)}
                                            themeV3
                                            data-testid="tennis-australia-confirmation.page.first-name-input"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <Input
                                            id="lastNameInput"
                                            field="lastName"
                                            label="Last Name"
                                            validate={createReactFormValidator(required)}
                                            themeV3
                                            data-testid="tennis-australia-confirmation.page.last-name-input"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <Input
                                            id="emailInput"
                                            field="email"
                                            label="Email"
                                            validate={createReactFormValidator(requiredEmail)}
                                            themeV3
                                            data-testid="tennis-australia-confirmation.page.email-input"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <PhoneNumber
                                            label="Phone Number"
                                            id="phone"
                                            field="phone"
                                            validate={createReactFormValidator([required, intlPhoneNumber])}
                                            defaultCountry="AU"
                                            themeV3
                                            data-testid="tennis-australia-confirmation.page.phone-input"
                                        />
                                    </div>
                                </div>

                                <GenderField
                                    values={values}
                                    touched={touched}
                                    getError={getError}
                                    getWarning={getWarning}
                                />

                                <div className="row">
                                    <div className="col-12">
                                        <div className="mb16">
                                            <label className="fw500" data-testid="tennis-australia-confirmation.page.dob-label">
                                                Date of Birth
                                            </label>
                                        </div>
                                        <div className="row">
                                            <div className="col-5 col-md-6">
                                                <Select
                                                    id="month"
                                                    field="month"
                                                    options={months}
                                                    validate={createReactFormValidator(required)}
                                                    optionsAsPrimitiveArray={true}
                                                    onChange={(val) => handleMonthChange(val)}
                                                    themeV3
                                                    label="Month"
                                                    data-testid="tennis-australia-confirmation.page.month-select"
                                                />
                                            </div>
                                            <div className="col-3 col-md-3">
                                                <Select
                                                    id="day"
                                                    field="day"
                                                    options={dayValues}
                                                    validate={createReactFormValidator(required)}
                                                    optionsAsPrimitiveArray={true}
                                                    themeV3
                                                    label="Day"
                                                    data-testid="tennis-australia-confirmation.page.day-select"
                                                />
                                            </div>
                                            <div className="col-4 col-md-3">
                                                <Select
                                                    id="year"
                                                    field="year"
                                                    options={years}
                                                    validate={createReactFormValidator(required)}
                                                    optionsAsPrimitiveArray={true}
                                                    themeV3
                                                    label="Year"
                                                    data-testid="tennis-australia-confirmation.page.year-select"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <label className={styles.termsLabel} data-testid="tennis-australia-confirmation.page.terms-label">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={handleOnTermsCheckChange}
                                        data-testid="tennis-australia-confirmation.page.terms-checkbox"
                                    />
                                    I agree to the UTR <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid="tennis-australia-confirmation.page.terms-tos-link">Terms of Service</a>
                                </label>

                                <button
                                    type="submit"
                                    className={`btn btn-primary btn-xl btn-block`}
                                    disabled={!checked || submitting}
                                    data-testid="tennis-australia-confirmation.page.submit-btn"
                                >
                                    {submitting ? 'LOADING...' : 'Confirm Profile'}
                                </button>
                            </form>
                        );
                    }}
                </Form>
            </div>
        </div>
    );
};

export default TennisAustraliaAuthConfirmationPage;
